import React, {
  useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import AsyncCreatableSelect from 'react-select/async-creatable';
import AsyncSelect from 'react-select/async';
import { CancelToken } from 'axios';
import { useField } from 'formik';

import { Api } from '@shell/shell-ui';
import Error from '@components/form/error';

export default function Autocomplete({
  url,
  name,
  label,
  optionLabel,
  disabled,
  filterField,
  isCreatable,
  sort,
}) {
  const cancelToken = useRef(null);
  const [field,, helpers] = useField(name);

  const loadItems = useCallback(async (fieldName) => {
    const params = {
      [filterField]: fieldName,
      size: 30,
    };

    if (sort) {
      params.sort = sort;
    }

    const query = new URLSearchParams(params);

    if (cancelToken.current) {
      cancelToken.current.cancel();
    }

    cancelToken.current = CancelToken.source();

    const { data } = await Api.noAuthRequest(`${ url }?${ query }`, 'get', {}, {
      cancelToken: cancelToken.current.token,
    });

    return [{
      label: 'Suggestions',
      options: data.content.map((item) => ({ value: item.id, label: item[optionLabel] })),
    }];
  }, [optionLabel]);

  const setValue = useCallback((value) => {
    helpers.setValue(value);
    helpers.setTouched(true);
  }, [helpers]);

  return (
    <div className='input__wrapper'>
      <label>
        <span className='input__wrapper__label'>{label}</span>
        <div className='input__wrapper__input'>
          {isCreatable ? (
            <AsyncCreatableSelect
              isClearable
              isDisabled={disabled}
              value={field.value}
              cacheOptions
              defaultOptions
              loadOptions={loadItems}
              createOptionPosition='first'
              placeholder='Select or type in...'
              formatCreateLabel={(v) => `Select: ${ v }`}
              isValidNewOption={(value, b, c) => (
                !c[0]?.options?.filter((i) => i.label === value).length && !!value
              )}
              onChange={setValue}
              classNamePrefix='react-select'
            />
          ) : (
            <AsyncSelect
              value={field.value}
              isClearable
              cacheOptions
              defaultOptions
              loadOptions={loadItems}
              isValidNewOption={(value, b, c) => (
                !c[0]?.options?.filter((i) => i.label === value).length && !!value
              )}
              onChange={setValue}
              classNamePrefix='react-select'
            />
          )}
        </div>
      </label>
      <Error name={field?.name || name} />
    </div>
  );
}

Autocomplete.defaultProps = {
  isCreatable: false,
  disabled: false,
  label: '',
  sort: '',
};

Autocomplete.propTypes = {
  filterField: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  optionLabel: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isCreatable: PropTypes.bool,
  label: PropTypes.string,
  sort: PropTypes.string
};
